import { ToastType } from '@components/toast';
import { UserPayerRecordPostRequestBody, UserPayerRecordPostResponse } from '@lio/api-types';
import { userQueryKey } from '@services/user/keys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useSnackbar } from 'notistack';

export const useCreateBillingAccountPayerRecord = ({
  userId,
  displayToasts = true,
}: {
  userId: string;
  displayToasts?: boolean;
}) => {
  const client = useClient();
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();
  const mutationFn = async (requestBody: UserPayerRecordPostRequestBody) => {
    const url = `user/${userId}/payer`;
    const res = await client.post(url, {
      json: requestBody,
    });
    return res.json<UserPayerRecordPostResponse>();
  };
  const result = useMutation({
    mutationFn,
    onSuccess: async () => {
      if (displayToasts) {
        enqueueSnackbar('Payment setup completed successfully', {
          variant: ToastType.Info,
        });
      }

      // clear out user cache (so payer id is updated)
      await queryClient.invalidateQueries({
        queryKey: userQueryKey.currentUser,
      });

      return true;
    },
    onError: async (e) => {
      const message = await getApiErrorMessage(
        e,
        'There was an issue creating your account. Please contact support for more information',
      );
      if (displayToasts) {
        enqueueSnackbar(message, {
          variant: ToastType.Error,
        });
      }
    },
  });
  return { ...result, createBillingAccountPayerRecord: result.mutateAsync };
};
